'use client';

import * as Sentry from '@sentry/nextjs';
import { differenceInDays } from 'date-fns';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { getABTestVersion } from '@/helpers/abTesting';
import { CookieType } from '@/helpers/common';
import { updateTSGData } from '@/helpers/topSpotGame';
import { groupMemberVerified } from '@/helpers/user';
import {
  NotVerifiedWithdrawalABTestVersion,
  WITHDRAWAL_MODAL_AB_TEST,
  WITHDRAWAL_MODAL_AB_TEST_CONFIG,
} from '@/helpers/withdrawalABTest';
import { useInitStore } from '@/hooks';
import { useCustomerIO } from '@/hooks/3dParty/customerIO';
import { useHotjar } from '@/hooks/3dParty/hotjar';
import { useIntercom } from '@/hooks/3dParty/intercom';
import { useOneSignal } from '@/hooks/3dParty/oneSignal';
import { useAuthCookies } from '@/hooks/authCookies';
import { pickProperties, useCookie } from '@/hooks/common';
import useEstonia from '@/hooks/estonia';
import { useGamesFiltration } from '@/hooks/gamesFiltration';
import { usePaymentsConfig } from '@/hooks/payments';
import { useRealTimeService } from '@/hooks/useRealTimeService';
import { updateBonanzaData } from '@/modules/promotions';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';

const USER_REFRESH_INTERVAL = 60e3;
const CONFIRMATION_EMAIL_ROUTE = '/users/confirmation';
const SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS = 7;

interface StoreClientProps {
  isMobileDevice: boolean;
  language: string;
}

export const StoreClient = ({ isMobileDevice, language }: StoreClientProps) => {
  const { initCustomerIO, resetCustomerIO } = useCustomerIO();

  const { isEstonia } = useEstonia();

  useEffect(() => {
    useCommonStore.setState({ isMobileDevice, pendingStore: false });
  }, [isMobileDevice]);

  const pathname = usePathname();
  const {
    currencies,
    isAuthenticated,
    pendingUser,
    user,
    countryCode,
    cmsABTests,
    topSpotCMSData,
    fetchUser,
    refreshUser,
    fetchAvatar,
    fetchFavoriteGames,
    getCompointsRatesMoney,
    setCountryCodeData,
    fetchInfoProject,
  } = useCommonStore(
    useShallow((state) => ({
      currencies: state.currencies,
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      user: state.user,
      countryCode: state.countryCode,
      cmsABTests: state.cmsABTests,
      topSpotCMSData: state.topSpotCMSData,
      fetchUser: state.fetchUser,
      refreshUser: state.refreshUser,
      fetchAvatar: state.fetchAvatar,
      fetchFavoriteGames: state.fetchFavoriteGames,
      getCompointsRatesMoney: state.getCompointsRatesMoney,
      setCountryCodeData: state.setCountryCodeData,
      fetchInfoProject: state.fetchInfoProject,
    }))
  );

  useInitStore(language);

  useEffect(() => {
    fetchInfoProject();
  }, [fetchInfoProject]);

  useEffect(() => {
    setCountryCodeData(isEstonia);
  }, [currencies, isEstonia, setCountryCodeData]);

  useEffect(() => {
    if (!isAuthenticated) return;
    getCompointsRatesMoney();
  }, [getCompointsRatesMoney, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAvatar();
  }, [fetchAvatar, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchFavoriteGames();
  }, [fetchFavoriteGames, isAuthenticated]);

  useEffect(() => {
    updateBonanzaData(language, user);
  }, [language, user]);

  useEffect(() => {
    if (!topSpotCMSData || !user) return;
    updateTSGData(topSpotCMSData, user);
  }, [topSpotCMSData, user]);

  useEffect(() => {
    if (user) return;
    fetchUser(REASON_FOR_UPDATING_USER.VISIT);
  }, [user, fetchUser]);

  useEffect(() => {
    if (pendingUser) return;
    fetchInfoProject();
  }, [pendingUser, user, fetchInfoProject]);

  useEffect(() => {
    if (!user || !groupMemberVerified(user)) {
      return useCommonStore.setState({ isVerifyMessage: false });
    }

    const version = getABTestVersion(
      WITHDRAWAL_MODAL_AB_TEST,
      WITHDRAWAL_MODAL_AB_TEST_CONFIG.versions,
      cmsABTests
    );

    useCommonStore.setState({
      isVerifyMessage:
        version === NotVerifiedWithdrawalABTestVersion.B_WITH_MESSAGE,
    });
  }, [cmsABTests, user]);

  useRealTimeService();

  useGamesFiltration();

  usePaymentsConfig();

  useEffect(() => {
    initCustomerIO();
    return () => resetCustomerIO();
  }, [initCustomerIO, resetCustomerIO]);

  useIntercom();

  useOneSignal();

  useHotjar();

  useEffect(() => {
    if (!isAuthenticated) return;

    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Logged User Id: ${user?.id}`,
      level: 'info',
    });

    Sentry.setUser({ id: user?.id?.toString() });
  }, [user?.id, isAuthenticated]);

  useAuthCookies(isAuthenticated);
  useCookie({
    name: 'userDetails',
    value: {
      type: CookieType.Object,
      data: pickProperties(user, [
        'id',
        'email',
        'statuses',
        'language',
        'country',
      ]),
    },
    days: 90,
    encryptKey: 'QExMyj2jKBnWDGpv',
    withReplace: true,
  });

  useEffect(() => {
    if (
      !isAuthenticated ||
      pendingUser ||
      user?.confirmedAt ||
      pathname === CONFIRMATION_EMAIL_ROUTE
    ) {
      useCommonStore.setState({ isEmailConfirmationPopup: false });

      if (user?.confirmedAt || pathname === CONFIRMATION_EMAIL_ROUTE) {
        localStorage.removeItem(STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP);
      }
    }

    if (isAuthenticated && !user?.confirmedAt) {
      const lastSeenEmailConfirmationPopup = useLocalStorageStore
        .getState()
        .get(STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP);

      const lastSeenDate =
        typeof lastSeenEmailConfirmationPopup === 'string' ||
        typeof lastSeenEmailConfirmationPopup === 'number'
          ? new Date(lastSeenEmailConfirmationPopup)
          : null;

      const isEmailConfirmationPopup =
        !lastSeenDate ||
        differenceInDays(new Date(), lastSeenDate) >=
          SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS;

      useCommonStore.setState({
        isEmailConfirmationPopup,
      });
    }
  }, [isAuthenticated, pendingUser, user, countryCode, pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => refreshUser(), USER_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUser]);

  return null;
};
